import AuthGuard from '@/components/feature/AuthGuard';
import { useAuth } from '@/hooks/useAuth';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import BillingToggle from './components/BillingToggle';

const navItems = [
  { path: '/dashboard', icon: 'ri-dashboard-line', label: 'Dashboard' },
  { path: '/calendar', icon: 'ri-calendar-line', label: 'Calendar' },
  { path: '/statistics', icon: 'ri-bar-chart-line', label: 'Statistics' },
  { path: '/call-log', icon: 'ri-phone-line', label: 'Call Log' },
  { path: '/habits', icon: 'ri-list-check-3', label: 'Habits' },
  { path: '/settings', icon: 'ri-settings-3-line', label: 'Settings' },
  { path: '/admin', icon: 'ri-shield-user-line', label: 'Admin' },
];

interface AdminUserRow {
  id: string;
  email: string | null;
  full_name: string | null;
  phone_number: string | null;
  role: string;
  language: string | null;
  timezone: string | null;
  created_at: string;
}

const roleBadge: Record<string, string> = {
  super_admin: 'bg-rose-50 text-rose-700 border-rose-200',
  admin: 'bg-amber-50 text-amber-700 border-amber-200',
  user: 'bg-background-200 text-foreground-600 border-background-300/60',
};

function AdminSidebar() {
  const { user, signOut } = useAuth();
  const location = useLocation();

  return (
    <aside className="w-64 bg-background-100 border-r border-background-200/60 flex flex-col flex-shrink-0">
      <div className="h-16 flex items-center gap-3 px-5 border-b border-background-200/60">
        <img src="/icon-192.png" alt="Zman logo" className="w-9 h-9 rounded-lg flex-shrink-0" />
        <div className="min-w-0">
          <h1 className="text-sm font-heading font-semibold text-foreground-950 truncate">Zman</h1>
          <p className="text-xs text-foreground-500">by CodeLab</p>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition whitespace-nowrap cursor-pointer ${
                isActive
                  ? 'bg-primary-100 text-primary-700 border-l-[3px] border-primary-500'
                  : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-900 border-l-[3px] border-transparent'
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} ${isActive ? 'text-primary-600' : 'text-foreground-400'}`} />
              </span>
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-background-200/60">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
            {user?.user_metadata?.full_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground-900 truncate">
              {user?.user_metadata?.full_name || user?.email?.split('@')[0]}
            </p>
            <p className="text-xs text-foreground-500 truncate">{user?.email}</p>
          </div>
          <button
            onClick={() => signOut()}
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-700 hover:bg-background-200 transition cursor-pointer whitespace-nowrap"
            title="Sign out"
          >
            <i className="ri-logout-box-r-line" />
          </button>
        </div>
      </div>
    </aside>
  );
}

function AdminContent() {
  const [users, setUsers] = useState<AdminUserRow[] | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    const { data, error: loadError } = await supabase
      .from('users')
      .select('id, email, full_name, phone_number, role, language, timezone, created_at')
      .order('created_at', { ascending: true });
    if (loadError) {
      setError(loadError.message);
      return;
    }
    setUsers((data || []) as AdminUserRow[]);
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const changeRole = async (userId: string, role: string) => {
    setSavingId(userId);
    setError('');
    const { error: updateError } = await supabase
      .from('users')
      .update({ role, updated_at: new Date().toISOString() })
      .eq('id', userId);
    setSavingId(null);
    if (updateError) {
      setError(`Failed to update role: ${updateError.message}`);
      return;
    }
    setUsers((prev) => prev?.map((u) => (u.id === userId ? { ...u, role } : u)) ?? prev);
  };

  const filtered = (users || []).filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (u.email || '').toLowerCase().includes(q) ||
      (u.full_name || '').toLowerCase().includes(q) ||
      (u.phone_number || '').includes(q)
    );
  });

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      <header className="h-16 flex items-center justify-between px-4 md:px-6 border-b border-background-200/60 bg-background-50 flex-shrink-0">
        <h2 className="text-sm font-medium text-foreground-600">Admin — User Management</h2>
      </header>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {error && (
          <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        <BillingToggle />

        <div className="flex items-center justify-between mb-4 gap-3">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
            />
          </div>
          <span className="text-xs text-foreground-500 whitespace-nowrap">
            {filtered.length} of {users?.length ?? 0} users
          </span>
        </div>

        {!users ? (
          <div className="space-y-2 animate-pulse">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-14 rounded-lg bg-background-200/60" />
            ))}
          </div>
        ) : (
          <div className="bg-background-100 rounded-lg border border-background-200/60 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/60 text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden md:table-cell">Phone</th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden lg:table-cell">Timezone</th>
                  <th className="px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Role</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-b border-background-200/40 last:border-0 hover:bg-background-200/40 transition">
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground-900">{u.full_name || '—'}</p>
                      <p className="text-xs text-foreground-500">{u.email}</p>
                    </td>
                    <td className="px-4 py-3 text-foreground-600 hidden md:table-cell">{u.phone_number || '—'}</td>
                    <td className="px-4 py-3 text-foreground-600 text-xs hidden lg:table-cell">{u.timezone || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${roleBadge[u.role] || roleBadge.user}`}>
                          {u.role}
                        </span>
                        <select
                          value={u.role}
                          disabled={savingId === u.id}
                          onChange={(e) => changeRole(u.id, e.target.value)}
                          className="text-xs rounded-md border border-background-300 bg-background-50 text-foreground-700 px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-primary-400 cursor-pointer disabled:opacity-50"
                        >
                          <option value="user">user</option>
                          <option value="admin">admin</option>
                          <option value="super_admin">super_admin</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-foreground-400 text-sm">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-3 text-[11px] text-foreground-400">
          Role changes are enforced server-side: only admins can update roles, and the
          prevent_role_escalation trigger blocks unauthorized promotion.
        </p>
      </div>
    </div>
  );
}

export default function AdminPage() {
  return (
    <AuthGuard requireAdmin>
      <div className="flex h-screen bg-background-50">
        <AdminSidebar />
        <AdminContent />
      </div>
    </AuthGuard>
  );
}
